import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { Button } from "@/components/ui/button";
import {
  Bubble,
  BubbleRow,
  TypingDots,
} from "@/chat/components/chat/Bubble";
import { ChatLayout } from "@/chat/components/chat/ChatLayout";
import { ThemeAndLanguageSwitcher } from "@/components/molecules/theme-language";
import {
  PROGRESS_STEPS,
  type OnboardingStep,
} from "@/features/onboarding/types";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * Onboarding, dressed as a conversation.
 *
 * The product is a phone assistant, and the setup now looks like talking to
 * one: the agent asks, the client answers, and the answers stay on screen as
 * their own bubbles. That reframes six screens of configuration as one
 * conversation you are already halfway through — the completed turns above are
 * doing the reassurance work the "nothing is final" strapline used to attempt.
 *
 * It borrows the web-chat's shell (`ChatLayout`) and cheats in two places, both
 * on purpose for now:
 *
 *  - **no composer.** Every answer is a button or a field inside a block, so
 *    `ChatLayout.input` is left out rather than filled with a dead text box.
 *  - **interactive bubbles.** The current question's answer is a block of real
 *    controls sitting where a bubble would be. The web-chat's `ChatMessage`
 *    only knows how to render a `SessionMessage`, so these are composed here
 *    from the web-chat's own `Bubble` instead of being forced through that type.
 */

/** One completed exchange, replayed above the current question. */
export type OnboardingTurn = {
  step: OnboardingStep;
  question: string;
  answer: string;
};

type ChatContextValue = {
  turns: OnboardingTurn[];
  agentName?: string;
  avatarId?: string;
  /** "Terminer plus tard" in the header; absent where there is nothing left to finish. */
  onLeave?: () => void;
};

const ChatContext = createContext<ChatContextValue>({ turns: [] });

export const OnboardingChatProvider = ChatContext.Provider;

export const useOnboardingChat = () => useContext(ChatContext);

/** The agent's face in the thread: the photo the client is choosing. */
function ThreadAvatar() {
  const { agentName, avatarId } = useOnboardingChat();
  return (
    <AgentAvatar
      avatarId={avatarId}
      name={agentName}
      size="lg"
      className="mb-1 size-7"
    />
  );
}

/** Agent side: the web-chat's agent bubble. */
export function AgentBubble({ children }: { children: ReactNode }) {
  return (
    <BubbleRow side="agent" className="animate-fade-in" avatar={<ThreadAvatar />}>
      <Bubble side="agent">
        <p className="text-sm leading-relaxed">{children}</p>
      </Bubble>
    </BubbleRow>
  );
}

/** The client's side, for turns they have already answered. */
export function UserBubble({ children }: { children: ReactNode }) {
  return (
    <BubbleRow side="user" className="animate-fade-in">
      <Bubble side="user">
        <p className="text-sm leading-relaxed whitespace-pre-wrap">
          {children}
        </p>
      </Bubble>
    </BubbleRow>
  );
}

/**
 * "The agent is typing", for the beat before each question.
 *
 * The web-chat's dots in the web-chat's bubble, but with this thread's avatar: the
 * web-chat's `TypingIndicator` draws the tenant's face (a logo), and the whole point
 * here is that the face the client is choosing is the one that answers them.
 *
 * It is theatre — the next question is already in memory — but it is the
 * difference between a form that swaps screens and a conversation with someone
 * on the other end, and it costs a few hundred milliseconds once per step.
 */
export function TypingBubble() {
  return (
    <BubbleRow side="agent" className="animate-fade-in" avatar={<ThreadAvatar />}>
      <Bubble side="agent">
        <TypingDots />
      </Bubble>
    </BubbleRow>
  );
}

/**
 * The interactive answer: real controls where a bubble would be.
 *
 * Aligned to the client's side and given a plain surface rather than the
 * primary fill — it is theirs to fill in, but it is a form, and a form on a
 * saturated background is unreadable.
 */
export function BlockBubble({ children }: { children: ReactNode }) {
  return (
    <BubbleRow side="user" className="animate-fade-in">
      <Bubble side="user" tone="plain" className="w-full max-w-none p-4">
        {children}
      </Bubble>
    </BubbleRow>
  );
}

/**
 * The frame: agent identity, progress, and the thread.
 *
 * The header carries the agent being configured — avatar and name update live
 * as the client picks them, so the thing they are building is looking back at
 * them the whole way through.
 */
export function OnboardingChatShell({
  step,
  children,
}: {
  step: OnboardingStep;
  children: ReactNode;
}) {
  const { t } = useTranslation("onboarding");
  const { turns, agentName, avatarId, onLeave } = useOnboardingChat();

  const endRef = useRef<HTMLDivElement>(null);
  const currentIndex = PROGRESS_STEPS.indexOf(step);

  // The agent "types" its question before it appears. Keyed on the step, so
  // every new question gets the beat and nothing else re-triggers it.
  const [typing, setTyping] = useState(true);
  useEffect(() => {
    setTyping(true);
    const timer = setTimeout(() => setTyping(false), 700);
    return () => clearTimeout(timer);
  }, [step]);

  // Land on the newest question, the way a chat does.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [step, turns.length, typing]);

  return (
    <ChatLayout
      className="md:h-[min(760px,90vh)] md:max-w-xl"
      header={
        <div className="border-b bg-background/80 backdrop-blur">
          <div className="flex items-center gap-3 p-4">
            <AgentAvatar
              avatarId={avatarId}
              name={agentName}
              size="lg"
              className="size-10 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">
                {agentName || t("chat.agentFallback")}
              </p>
              <p className="text-muted-foreground truncate text-xs">
                {currentIndex >= 0
                  ? t("progress.stepCount", {
                      current: currentIndex + 1,
                      total: PROGRESS_STEPS.length,
                    })
                  : t("chat.settingUp")}
              </p>
            </div>

            {/* The way out: the wizard had none. It keeps what was answered
                and the back-office offers to resume. */}
            {onLeave && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground shrink-0"
                onClick={onLeave}
              >
                {/* The agent's name keeps the room on a phone. */}
                <span className="sm:hidden">{t("actions.laterShort")}</span>
                <span className="hidden sm:inline">{t("actions.later")}</span>
              </Button>
            )}

            {/* Inside the frame, alongside the agent's identity: it is the
                  one control that changes the conversation itself. */}
            <ThemeAndLanguageSwitcher />
          </div>

          {currentIndex >= 0 && (
            <div className="flex gap-1 px-4 pb-3" aria-hidden>
              {PROGRESS_STEPS.map((visibleStep, index) => (
                <div
                  key={visibleStep}
                  className={cn(
                    "h-1 flex-1 rounded-full transition-colors",
                    index <= currentIndex ? "bg-primary" : "bg-slate-500/20",
                  )}
                />
              ))}
            </div>
          )}
        </div>
      }
      messages={
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {turns.map((turn) => (
            <div key={turn.step} className="space-y-2">
              <AgentBubble>{turn.question}</AgentBubble>
              <UserBubble>{turn.answer}</UserBubble>
            </div>
          ))}

          {typing ? <TypingBubble /> : children}

          <div ref={endRef} />
        </div>
      }
    />
  );
}
